/**
 * @friskai/frisk-js Error Classes
 *
 * Custom error types for the Frisk SDK.
 */

/**
 * Options for FriskError constructor
 */
export interface FriskErrorOptions extends ErrorOptions {
  /** Additional context or metadata about the error */
  context?: Record<string, unknown>
}

/**
 * Base error class for all Frisk SDK errors
 */
export class FriskError extends Error {
  /** Additional context or metadata about the error */
  readonly context?: Record<string, unknown>

  constructor(message: string, options?: FriskErrorOptions) {
    super(message, options)
    this.name = 'FriskError'
    this.context = options?.context
    // Maintains proper stack trace for where our error was thrown (only in V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor)
    }
  }
}

/**
 * Error thrown when the API key is missing or invalid
 */
export class MissingAPIKeyError extends FriskError {
  constructor(options?: FriskErrorOptions) {
    super(
      'Frisk API key is required. Provide it via the apiKey parameter or set the FRISK_API_KEY environment variable.',
      options,
    )
    this.name = 'MissingAPIKeyError'
  }
}

/**
 * Error thrown when the base URL is missing
 */
export class MissingBaseURLError extends FriskError {
  constructor(options?: FriskErrorOptions) {
    super(
      'Frisk base URL is required. Provide it via the baseUrl parameter or set the FRISK_BASE_URL environment variable.',
      options,
    )
    this.name = 'MissingBaseURLError'
  }
}

/**
 * Error thrown when the telemetry endpoint is missing
 */
export class MissingOtlpEndpointError extends FriskError {
  constructor(options?: FriskErrorOptions) {
    super(
      'Frisk telemetry endpoint is required. Provide it via the telemetryEndpoint parameter or set the FRISK_TELEMETRY_ENDPOINT environment variable.',
      options,
    )
    this.name = 'MissingTelemetryEndpointError'
  }
}

/**
 * Error thrown when a session is not found
 */
export class SessionNotFoundError extends FriskError {
  constructor(sessionId: string, options?: FriskErrorOptions) {
    super(
      `Session with ID ${sessionId} was not found in the registry.`,
      options,
    )
    this.name = 'SessionNotFoundError'
  }
}

/**
 * Error thrown when Frisk session context is missing
 */
export class MissingFriskSessionContextError extends FriskError {
  constructor(options?: FriskErrorOptions) {
    super(
      'Frisk session context is missing. Ensure you have created a session with frisk.session() and passed it to the context.',
      options,
    )
    this.name = 'MissingFriskSessionContextError'
  }
}

/**
 * Error thrown when the Frisk session is invalid
 */
export class InvalidFriskSessionError extends FriskError {
  constructor(options?: FriskErrorOptions) {
    super(
      'Invalid Frisk session. The session may have expired or been invalidated.',
      options,
    )
    this.name = 'InvalidFriskSessionError'
  }
}

/**
 * Error thrown when Frisk SDK methods are called before initialization
 */
export class FriskNotInitializedError extends FriskError {
  constructor(options?: FriskErrorOptions) {
    super(
      'Frisk SDK is not initialized. Call frisk.connect() before using the SDK.',
      options,
    )
    this.name = 'FriskNotInitializedError'
  }
}

/**
 * Error thrown when a tool schema type is not supported
 */
export class UnsupportedToolSchemaError extends FriskError {
  constructor(expectedType: string, options?: FriskErrorOptions) {
    super(
      `Unsupported tool schema type. Expected ${expectedType} schema but received an incompatible schema type.`,
      options,
    )
    this.name = 'UnsupportedToolSchemaError'
  }
}

/**
 * Error thrown when tool call evaluation fails
 */
export class ToolCallEvaluationError extends FriskError {
  readonly originalError: Error

  constructor(originalError: Error, options?: FriskErrorOptions) {
    const message = `An error occurred while evaluating the tool call. 
The tool call will be allowed by default.
Original error:
    ${originalError}`

    super(message, { ...options, cause: originalError })
    this.name = 'ToolCallEvaluationError'
    this.originalError = originalError
  }
}

/**
 * Error thrown when the access token is invalid
 */
export class InvalidAccessTokenError extends Error {
  constructor(message = 'Invalid access token format') {
    super(message)
    this.name = 'InvalidAccessTokenError'
  }
}

/**
 * Error thrown when the Frisk base URL is not reachable
 */
export class FriskBaseURLNotFoundError extends Error {
  constructor(message = 'Frisk base URL not reachable') {
    super(message)
    this.name = 'FriskBaseURLNotFoundError'
  }
}

/**
 * Error thrown when the API key is invalid
 */
export class FriskInvalidAPIKeyError extends Error {
  constructor(message = 'Invalid Frisk API key') {
    super(message)
    this.name = 'FriskInvalidAPIKeyError'
  }
}

/**
 * Error thrown when the server response is unexpected
 */
export class UnexpectedFriskServerResponseError extends Error {
  constructor(message = 'Unexpected response from Frisk server') {
    super(message)
    this.name = 'UnexpectedFriskServerResponseError'
  }
}

/**
 * Error thrown when tool policy evaluation is attempted without the native extension available.
 */
export class MissingToolPoliciesExtensionError extends FriskError {
  constructor(options?: FriskErrorOptions) {
    super(
      // todo! Message
      'decide_tool_call requires the tool policies extension. Install and register the @friskai/frisk-js-tool-policies extension; calling frisk.connect() alone is not sufficient.',
      options,
    )
    this.name = 'MissingToolPoliciesExtensionError'
  }
}

/** Raised when tool-call span APIs are used before span initialization. */
export class ToolCallSpanNotInitializedError extends Error {
  constructor(
    message = 'Tool call span has not been initialized. Call enter() first.',
  ) {
    super(message)
    this.name = 'ToolCallSpanNotInitializedError'
  }
}
